import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { ReactComponent as AddAttachmentIcon } from '../../../assets/svg/add-attachment.svg';
import { ReactComponent as AttachmentAdded } from '../../../assets/icons/svg/colored-icons/ic-colored-attachment.svg';
import { ReactComponent as RemoveFileIcon } from '../../../assets/svg/remove-file.svg';
import Spinner, { SpinnerColor } from '../../../generic-components/Spinner';

interface IProps {
  onFilesChange: (filesList: FileList) => void;
  onFileClear?: () => void;
  IconAsProp?: any;
  disclaimer?: string;
  maxFileSizeInMb?: number;
  acceptMultiFile?: boolean;
  isMinimalMode?: boolean;
  onFileClick?: () => void;
  acceptedTypes: string[];
  isLoading?: boolean;
  actionTitle?: string;
  onError?: (errorMessage: string) => void;
  isDisabled?: boolean;
}

export const DropArea = ({
  onFilesChange,
  IconAsProp,
  disclaimer,
  maxFileSizeInMb,
  acceptMultiFile,
  isMinimalMode,
  onFileClick,
  onFileClear,
  acceptedTypes,
  isLoading,
  isDisabled,
  actionTitle,
  onError,
}: IProps) => {
  const [isDragEnter, setIsDragEnter] = useState<boolean>(false);
  const [filesUploaded, setFilesUploaded] = useState<FileList | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const filePicker = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const ONE_MB_IN_BYTES: number = 1048576;

  const uploadedFileName = useMemo(() => filesUploaded?.[0]?.name || '', [filesUploaded]);

  const typesToString = useMemo(() => acceptedTypes.join(','), [acceptedTypes]);

  const handleDrop = (e: any) => {
    e.preventDefault();
    if (isDisabled) return;
    const { files } = e.dataTransfer;
    setFilesUploaded(files);
    setIsDragEnter(false);
  };

  const handleWrapperClick = () => {
    if (isMinimalMode && onFileClick && uploadedFileName) {
      onFileClick();
    } else if (isMinimalMode) {
      filePicker?.current?.click();
    }
  };

  const handleManualInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e?.target?.files || !e?.target?.files.length) return;
    setFilesUploaded(e.target.files);
  };

  const validateFilesSize = (): boolean => {
    if (!maxFileSizeInMb) return true;
    const filesArray = Array.from(filesUploaded || []);
    return !filesArray.some((file) => file.size > maxFileSizeInMb * ONE_MB_IN_BYTES);
  };

  const handleClearFiles = () => {
    if (filePicker?.current) {
      filePicker.current.value = '';
      setFilesUploaded(null);
      if (onFileClear) onFileClear();
    }
  };

  const handleIconClick = () => {
    if (uploadedFileName) return;
    filePicker?.current?.click();
  };

  const validateFileTypes = () => {
    let isValid: boolean = true;
    // @ts-ignore
    const filesList = Array.from(filesUploaded);
    // eslint-disable-next-line consistent-return
    filesList.forEach((f) => {
      const cleanFileType = f.type.split('/')[1];
      const isAccepted = acceptedTypes.some((acceptedType: string) => acceptedType.includes(cleanFileType));
      if (!isAccepted) {
        isValid = false;
      }
    });
    return isValid;
  };

  useEffect(() => {
    if (filesUploaded?.length && validateFilesSize()) {
      if (!validateFileTypes()) {
        setFilesUploaded(null);
        const error = t('invalidFileType', {
          type: 'JPEG, PDF, PNG',
        });
        setErrorMessage(error);
        if (onError) {
          onError(error);
        }
        return;
      }
      setErrorMessage('');
      onFilesChange(filesUploaded);
    } else if (!validateFilesSize()) {
      const error = t('DROP_AREA.SIZE_LIMIT_ERROR', { fileSizeInMB: maxFileSizeInMb });
      setFilesUploaded(null);

      setErrorMessage(error);
      if (onError) {
        onError(error);
      }
    }
  }, [filesUploaded]);

  return (
    <div
      tabIndex={0}
      role='button'
      onKeyDown={() => {}}
      className={`relative w-full h-full 
      ${isDragEnter ? 'bg-bw03' : uploadedFileName ? 'bg-bw01' : 'bg-bw00'} ${isMinimalMode ? 'flex items-center px-[16px]' : 'flex flex-col items-center justify-center px-[30px]'} py-[14px] ${uploadedFileName ? 'outline cursor-pointer' : 'outline-dashed'} outline-2 ${errorMessage ? 'outline-red02' : 'outline-action03'} shadow-md hover:bg-bw02 rounded-md`}
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={() => setIsDragEnter(true)}
      onDragLeave={() => setIsDragEnter(false)}
      onClick={handleWrapperClick}
    >
      {isMinimalMode ? (
        <div className='flex items-center gap-[14px] w-full'>
          <Button
            onClick={handleIconClick}
            className=''
            id='filePicker'
            childElement={uploadedFileName ? <AttachmentAdded /> : <AddAttachmentIcon />}
          />
          <input
            disabled={isDisabled}
            accept={typesToString}
            multiple={acceptMultiFile}
            onChange={handleManualInput}
            ref={filePicker}
            className='hidden'
            type='file'
            data-testid='createBill-add-an-attachment'
          />
          {uploadedFileName && (
            <Button
              onClick={handleClearFiles}
              className='absolute right-[16px] top-[50%] translate-y-[-50%]'
              id='clearFiles'
              childElement={<RemoveFileIcon />}
            />
          )}
          {uploadedFileName ? (
            <Heading title={uploadedFileName} className='font-metricR text-bw08 text-[16px] truncate max-w-[76%]' />
          ) : (
            <div className='flex flex-col'>
              <Button
                onClick={() => filePicker?.current?.click()}
                className='w-fit'
                id='filePicker'
                childElement={
                  <Heading title={actionTitle || 'Add an attachment'} className=' font-metricR text-bw08 text-[16px]' />
                }
              />
              <Heading title={`${t('DROP_AREA.SUPPORTED_TYPES')}`} className='font-metricR text-bw07 text-[14px]' />
              {!onError && errorMessage && (
                <Heading title={errorMessage} className='font-metricR text-[12px] text-red02' />
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          {IconAsProp && <IconAsProp className='mb-[14px]' />}
          <div className='flex items-center gap-1 mb-[14px]'>
            <Heading title={t('DROP_AREA.MAIN_TITLE')} className='text-bw08 text-[16px] font-metricR' />
            <Button
              onClick={() => filePicker?.current?.click()}
              title={t('DROP_AREA.MANUAL_UPLOAD_TITLE')}
              className='text-bw08 hover:text-bw07 underline cursor-pointer text-[16px] font-metricRMedium'
              id=''
            />
            <input
              disabled={isDisabled}
              accept={typesToString}
              multiple={acceptMultiFile}
              onChange={handleManualInput}
              ref={filePicker}
              className='hidden'
              type='file'
              data-testid='createBill-upload-file-for-scanning'
            />
          </div>
          {maxFileSizeInMb && (
            <Heading
              title={(t('DROP_AREA.MAX_SIZE_LIMIT') as string).replace('|SIZE_VAL|', maxFileSizeInMb.toString())}
              className='text-bw06 text-[14px] font-metricR mb-[4px]'
            />
          )}
          <Heading
            title={`${t('DROP_AREA.SUPPORTED_TYPES')}`}
            className='text-bw06 text-[14px] font-metricR mb-[24px]'
          />
          {disclaimer && (
            <Heading type='HTML' title={disclaimer} className='text-bw06 text-[14px] font-metricR text-center' />
          )}
          {errorMessage && (
            <Heading
              title={errorMessage}
              className='font-metricR text-[12px] text-red02 mt-auto absolute left-[50%] translate-x-[-50%] bottom-[14px]'
            />
          )}
        </>
      )}

      {isLoading && (
        <div className='absolute inset-0 bg-bw08Opacity flex items-center justify-center' id='uploadLoader'>
          <Spinner spinnerColor={SpinnerColor.Blue} height='2rem' width='2rem' />
        </div>
      )}
    </div>
  );
};
